import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo } from '@/components/common/typography'
import { couponApplyToastMessage } from '@/components/domains/order2/coupon-apply-toast-message'
import { ModalOrderProductCouponSelect } from '@/components/domains/order2/modal-order-product-coupon-select'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import useOrderPageEventTracker from '@/containers/event-tracker/order.event-tracker'
import { useModal } from '@/containers/hooks'
import { useSetEstimateOrder } from '@/containers/order/set-estimate-order.hook'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { isDateBefore } from '@/utils/utilDate'
import { currencyText } from '@/utils/utilNumber'
import type { CouponForEstimateOrderScheme } from '@/apis/rest/generated/types'

interface OrderCouponSectionProps {
  countOfAvailableOrderCoupon: number
  isPartialOrderCouponDiscount: boolean
  orderCoupons: CouponForEstimateOrderScheme[] | undefined
  productSaleAmount: number | undefined
}

export const OrderCouponSection = ({
  countOfAvailableOrderCoupon,
  isPartialOrderCouponDiscount,
  productSaleAmount = 0,
  orderCoupons = [],
}: OrderCouponSectionProps) => {
  const dispatch = useDispatch()
  const { color } = useTheme()
  const { useModalProps } = useModal()

  const { trackViewOrderCouponNew, trackClickOrderCouponApplyNew } = useOrderPageEventTracker()

  const { setEstimateOrderByOrderCoupon } = useSetEstimateOrder()

  const appliedCoupon = orderCoupons?.find((coupon) => coupon.isApplied)

  const handleShowModal = () => {
    if (orderCoupons?.length <= 0) {
      dispatch(doSetGlobalToastContent({ content: '보유중인 쿠폰이 없어요' }))
      return
    }
    trackViewOrderCouponNew(orderCoupons, productSaleAmount)
    useModalProps.showModal()
  }

  const handleApplyCoupon = (couponId: number | undefined) => {
    const selectedCoupon = orderCoupons.find((coupon) => coupon.id === couponId)

    // 쿠폰 만료 시각 <= 현재 시각 => true
    if (selectedCoupon && isDateBefore(selectedCoupon.expiredAt, new Date().toISOString())) {
      dispatch(doSetGlobalToastContent({ content: '유효기간이 만료된 쿠폰이에요' }))
      setEstimateOrderByOrderCoupon(undefined)
      return
    } else {
      const message = couponApplyToastMessage({
        appliedCouponExist: !!appliedCoupon,
        selectedCouponExist: !!selectedCoupon,
      })
      if (message) {
        dispatch(doSetGlobalToastContent({ content: message }))
        setEstimateOrderByOrderCoupon(couponId)
      }
    }

    if (selectedCoupon) {
      trackClickOrderCouponApplyNew(orderCoupons, selectedCoupon, productSaleAmount)
    }
  }

  return (
    <>
      <StyledSectionTitle>
        <Typo variant={'Body1 Bold'}>주문 쿠폰</Typo>
      </StyledSectionTitle>
      <StyledContainer>
        <StyledSelectBox type="button" onClick={handleShowModal}>
          {appliedCoupon && (
            <Typo variant="Body3 Bold" color={color.red500}>
              {`${currencyText(appliedCoupon.appliedDiscountAmount)} 할인됨`}{' '}
              {isPartialOrderCouponDiscount && (
                <Typo variant="Body5 Regular" color={color.gray400}>
                  (일부 할인 불가 상품 제외)
                </Typo>
              )}
            </Typo>
          )}
          {!appliedCoupon &&
            (countOfAvailableOrderCoupon <= 0 ? (
              <Typo variant="Body1 regular" color={color.gray900}>
                사용 가능한 주문 쿠폰이 없어요
              </Typo>
            ) : (
              <StyledSelectText>
                <FlatIcon type={IconNameEnum.IcOrderCoupon} size={IconSizeEnum.Size16} width={IconSizeEnum.Size20} />
                <Typo variant="Body3 Bold" color={color.red500}>
                  {`사용 가능한 쿠폰이 ${countOfAvailableOrderCoupon}장 있어요.`}
                </Typo>
              </StyledSelectText>
            ))}
          <ImageAsset name="ic_chevron_down_gray700_24" size={24} svg />
        </StyledSelectBox>
        <Typo variant="Body3 Regular" color={color.gray600}>{`보유쿠폰 ${orderCoupons.length}장`}</Typo>
      </StyledContainer>
      <ModalOrderProductCouponSelect
        key={`order-coupon-${appliedCoupon?.appliedDiscountAmount}`}
        title="주문쿠폰 선택"
        useModalProps={useModalProps}
        appliedCoupon={appliedCoupon}
        couponList={orderCoupons}
        onSelectCoupon={handleApplyCoupon}
      />
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0 1.6rem 2rem;
`

const StyledSelectBox = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 5rem;
  border: 1px solid ${GlobalColorEnum.Gray100};
  border-radius: 0.8rem;
  box-sizing: border-box;
  padding: 1.6rem;
  cursor: pointer;
  background-color: ${(props) => props.theme.color.grayWhite};
`

const StyledSelectText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`
